import React from "react";

import { Input } from "shared/components/Form";
import { ActionLink } from "shared/components/Links";
import { RegularText } from "shared/components/Typography";

import {
  CosignMonospaceValue,
  CosignPmUuidEditActions,
  CosignPmUuidEditContainer,
  CosignPmUuidEditError,
  CosignPmUuidViewRow,
  CosignSummaryLabel,
} from "./styled";
import { useCosignPmUuidEdit } from "./useCosignPmUuidEdit";

interface CosignPmUuidSummaryProps {
  maskedPropertyManagerUuid?: string | null;
  propertyManagerUuid?: string | null;
}

/**
 * Dashboard header control for viewing and correcting the company PM UUID (PRODUCT-1134).
 */
const CosignPmUuidSummary: React.FC<CosignPmUuidSummaryProps> = ({
  maskedPropertyManagerUuid,
  propertyManagerUuid,
}) => {
  const {
    isEditing,
    draftUuid,
    inlineError,
    isSaving,
    setDraftUuid,
    startEditing,
    cancelEditing,
    saveDraft,
  } = useCosignPmUuidEdit({
    propertyManagerUuid,
  });

  const hasConfiguredPm = Boolean(
    propertyManagerUuid || maskedPropertyManagerUuid
  );

  return (
    <>
      <CosignSummaryLabel>Cosign account ID</CosignSummaryLabel>
      {isEditing ? (
        <CosignPmUuidEditContainer>
          <Input
            id="cosign-pm-uuid-input"
            name="cosignPropertyManagerUuid"
            value={draftUuid}
            onChange={(event) => setDraftUuid(event.target.value)}
            disabled={isSaving}
            data-testid="cosign-pm-uuid-input"
          />
          {inlineError ? (
            <CosignPmUuidEditError data-testid="cosign-pm-uuid-error">
              {inlineError}
            </CosignPmUuidEditError>
          ) : null}
          <CosignPmUuidEditActions>
            <ActionLink
              onClick={saveDraft}
              disabled={isSaving}
              data-testid="cosign-pm-uuid-save"
            >
              Save
            </ActionLink>
            <ActionLink
              onClick={cancelEditing}
              disabled={isSaving}
              data-testid="cosign-pm-uuid-cancel"
            >
              Cancel
            </ActionLink>
          </CosignPmUuidEditActions>
        </CosignPmUuidEditContainer>
      ) : (
        <CosignPmUuidViewRow>
          <CosignMonospaceValue data-testid="cosign-dashboard-pm-uuid">
            {maskedPropertyManagerUuid || "—"}
          </CosignMonospaceValue>
          <ActionLink
            onClick={startEditing}
            data-testid="cosign-pm-uuid-edit"
          >
            Edit
          </ActionLink>
        </CosignPmUuidViewRow>
      )}
      {!isEditing && !hasConfiguredPm ? (
        <RegularText weak>No Cosign account is configured yet.</RegularText>
      ) : null}
    </>
  );
};

export default CosignPmUuidSummary;
